import React from 'react'
import type { CommunityReaction } from '../services/communityService'
import { CommunityEmojiIcon } from './CommunityEmojiIcon'
import { COMMUNITY_EMOJI_PALETTE } from '../constants/emojis'

type CommunityCardReactionsRowProps = {
  reactions: CommunityReaction[]
  onToggleEmoji: (emoji: string) => void | Promise<void>
  className?: string
  renderAfter?: React.ReactNode
  maxVisibleReactions?: number
}

export function CommunityCardReactionsRow({
  reactions,
  onToggleEmoji,
  className,
  renderAfter,
  maxVisibleReactions,
}: CommunityCardReactionsRowProps) {
  const containerClassName = [
    'flex flex-wrap items-center gap-1 text-[12px] text-foreground/70',
    className || '',
  ]
    .filter(Boolean)
    .join(' ')

  const paletteIndex: Record<string, number> = React.useMemo(
    () =>
      COMMUNITY_EMOJI_PALETTE.reduce<Record<string, number>>((acc, emoji, index) => {
        acc[emoji] = index
        return acc
      }, {}),
    [],
  )

  const items = React.useMemo(
    () =>
      reactions
        .filter((r) => r.count > 0)
        .sort((a, b) => {
          const ia = paletteIndex[a.emoji] ?? Number.MAX_SAFE_INTEGER
          const ib = paletteIndex[b.emoji] ?? Number.MAX_SAFE_INTEGER
          return ia - ib
        }),
    [paletteIndex, reactions],
  )

  const { visibleItems, hiddenCount } = React.useMemo(() => {
    if (!items.length) {
      return { visibleItems: items, hiddenCount: 0 }
    }

    const max =
      typeof maxVisibleReactions === 'number' && maxVisibleReactions > 0
        ? maxVisibleReactions
        : null

    if (max == null || items.length <= max) {
      return { visibleItems: items, hiddenCount: 0 }
    }

    return {
      visibleItems: items.slice(0, max),
      hiddenCount: items.length - max,
    }
  }, [items, maxVisibleReactions])

  if (!items.length) {
    return (
      <div className={containerClassName}>
        {/* Renderiza apenas o elemento extra (por exemplo, botão de adicionar) quando não há reações */}
        {typeof renderAfter !== 'undefined' ? renderAfter : null}
      </div>
    )
  }

  return (
    <div className={containerClassName}>
      {visibleItems.map((item) => (
        <button
          key={item.emoji}
          type="button"
          onClick={() => onToggleEmoji(item.emoji)}
          className={[
            'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[12px] transition-colors',
            item.reactedByCurrentUser
              ? 'border-emerald-400 bg-emerald-500/10'
              : 'border-[#2b2b2d] bg-[#1a1a1c] hover:bg-[#29292c]',
          ].join(' ')}
        >
          <CommunityEmojiIcon emoji={item.emoji} loading="eager" showFallback />
          <span>{item.count}</span>
        </button>
      ))}
      {hiddenCount > 0 && (
        <div className="inline-flex items-center gap-1 rounded-full border border-[#2b2b2d] bg-[#1a1a1c] px-2 py-0.5 text-[12px] text-foreground/70">
          <span>+{hiddenCount}</span>
        </div>
      )}
      {typeof renderAfter !== 'undefined' ? renderAfter : null}
    </div>
  )
}
